import React from 'react'
import PropTypes from 'prop-types'
import { Layer, Feature, Popup, ZoomControl } from 'react-mapbox-gl'
import mapboxgl from 'mapbox-gl'
import ConfiguredMapboxGl, {
  MapboxStyle,
} from '../../helpers/ConfiguredMapboxGl'

class PropertiesMap extends React.Component {
  constructor(props) {
    super(props)

    this.initialCenter = this.getInitialCenter()
    this.defaultZoom = 10
    this.boundsOptions = { padding: 20, maxZoom: this.defaultZoom }
    this.markerLayout = {
      'icon-image': 'marker_15',
      'icon-size': 1.5,
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
    }
    this.state = {
      map: null,
      initialBoundsFit: true,
      activePropertyId: null,
    }
    this.onStyleLoad = this.onStyleLoad.bind(this) // bind here
  }

  componentDidMount() {
    if (this.mapContainer) {
      this.resizeObserver = new ResizeObserver(() => {
        if (this.state.map) {
          this.state.map.resize()
        }
      })
      this.resizeObserver.observe(this.mapContainer)
    }
  }

  componentWillUnmount() {
    if (this.resizeObserver && this.mapContainer) {
      this.resizeObserver.unobserve(this.mapContainer)
    }
  }

  onStyleLoad(map) {
    this.setState({ map })
    setTimeout(() => {
      map.resize()
      this.updateBounds() // call after resize
    }, 0)
  }

  onPropertyClick(id) {
    const { activePropertyId } = this.state
    const isCurrentlySelected = activePropertyId
      ? activePropertyId === id
      : false

    if (isCurrentlySelected) {
      this.setState({ activePropertyId: null })
    } else {
      this.setState({ activePropertyId: id })
    }
  }

  closePopover() {
    this.setState({ activePropertyId: null })
  }

  getBounds() {
    const positions = this.allPositions()
    const bounds = new mapboxgl.LngLatBounds()
    positions.forEach((position) => bounds.extend(position))
    return bounds
  }

  updateBounds() {
    const { map, initialBoundsFit } = this.state

    const bounds = this.getBounds()
    let fitBoundsOptions = { ...this.boundsOptions }

    if (initialBoundsFit) {
      fitBoundsOptions = { ...fitBoundsOptions, linear: false, duration: 1 }
      this.setState({ initialBoundsFit: false })
    }

    map.fitBounds(bounds, fitBoundsOptions)
  }

  coordinatesForProperty(property) {
    return [property.longitude, property.latitude]
  }

  findActiveProperty() {
    const { activePropertyId } = this.state

    return this.findProperty(activePropertyId)
  }

  findProperty(id) {
    const { properties } = this.props

    return properties.find((property) => property.id === id)
  }

  allPositions() {
    const { properties } = this.props

    return properties.map(
      (property) => new mapboxgl.LngLat(property.longitude, property.latitude)
    )
  }

  getInitialCenter() {
    const bounds = this.getBounds()
    return bounds.getCenter()
  }

  renderMarker(property) {
    const { id } = property
    const coordinates = this.coordinatesForProperty(property)

    return (
      <Feature
        onClick={() => this.onPropertyClick(id)}
        key={`property-${id}`}
        coordinates={coordinates}
      />
    )
  }

  renderMarkers() {
    const { properties } = this.props

    return properties.map((property) => this.renderMarker(property))
  }

  renderActivePopup() {
    const property = this.findActiveProperty()

    if (property) {
      const coordinates = this.coordinatesForProperty(property)

      return (
        <Popup coordinates={coordinates}>
          <div className="properties-map__popup">
            <button
              onClick={() => this.closePopover()}
              className="properties-map__popup__close"
            >
              X
            </button>
            <a className="properties-map__popup__image" href={property.href}>
              <img src={property.imageUrl} alt={property.title} />
            </a>
            <h2 className="properties-map__popup__title">
              <a href={property.href}>{property.title}</a>
            </h2>
            <p className="properties-map__popup__sub-title">
              {property.subtitle}
            </p>
          </div>
        </Popup>
      )
    }

    return null
  }

  render() {
    return (
      <div
        ref={(el) => (this.mapContainer = el)}
        style={{ width: '100%', height: '100%' }}
      >
        <ConfiguredMapboxGl
          style={MapboxStyle}
          onStyleLoad={this.onStyleLoad} // ✅ regular method, already bound
          containerStyle={{ height: '100%', width: '100%' }}
          movingMethod="easeTo"
          center={this.initialCenter}
          fitBoundsOptions={this.boundsOptions}
        >
          <ZoomControl position="top-left" />
          <Layer id="item-layer" type="symbol" layout={this.markerLayout}>
            {this.renderMarkers()}
          </Layer>
          {this.renderActivePopup()}
        </ConfiguredMapboxGl>
      </div>
    )
  }
}

PropertiesMap.defaultProps = {
  properties: [],
}

PropertiesMap.propTypes = {
  properties: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      latitude: PropTypes.number.isRequired,
      longitude: PropTypes.number.isRequired,
      title: PropTypes.string.isRequired,
      subtitle: PropTypes.string.isRequired,
      imageUrl: PropTypes.string,
      href: PropTypes.string.isRequired,
    })
  ),
}

export default PropertiesMap
